import React, { useMemo } from "react";
import SectionCard from "Explore/Pages/Overview/Components/SectionCard";
import * as d3 from 'd3';
import { SDG_INFO, getSdgIconUrl } from "./constants";

const SocialImpactCard = ({
  data,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSdgClick
}) => {
  const sdgs = data?.sdgs || [];
  const window = data?.window;

  // Active goals are those with at least 1 publication
  const activeSDGs = sdgs.filter(s => (s.papers || 0) > 0).length;

  // Prefer mapped_papers directly from backend if available (avoiding multi-SDG double counting)
  // Fall back to summing papers if mapped_papers is not provided (e.g. legacy endpoint)
  const totalPubs = data?.mapped_papers != null
    ? data.mapped_papers
    : sdgs.reduce((sum, s) => sum + (s.papers || 0), 0);

  const treemapLeaves = useMemo(() => {
    if (!sdgs || sdgs.length === 0) return [];

    const validData = sdgs.filter(d => (d.papers || 0) > 0);
    if (validData.length === 0) return [];

    const root = d3.hierarchy({ children: validData })
      .sum(d => d.papers || 0)
      .sort((a, b) => b.value - a.value);

    const treemapLayout = d3.treemap()
      .size([100, 100])
      .paddingInner(0.2)
      .round(false);

    treemapLayout(root);
    return root.leaves();
  }, [sdgs]);

  return (
    <SectionCard
      title="Social Impact"
      description={window?.start_year && window?.end_year ? `SDG Contributions (${window.start_year}-${window.end_year})` : "UN Sustainable Development Goals"}
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={data?.insufficient_data || sdgs.length === 0 || activeSDGs === 0}
      onRetry={onRetry}
      noPadding
    >
      <div className="flex flex-col">
        {/* Top Header Summary */}
        <div className="w-full flex justify-center bg-background pt-10 pb-6">
          <div className="w-full max-w-[1100px] flex flex-col md:flex-row items-center gap-8 md:gap-16 px-6">
            <h3 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-[#2a2a2a] whitespace-nowrap">
              The 17 Goals
            </h3>

            <div className="h-14 w-[2px] bg-[#53a9df] hidden md:block shrink-0"></div>

            <div className="flex-1 flex flex-wrap items-center justify-between w-full px-2 lg:px-8">
              <div className="flex flex-col items-center">
                <span className="text-3xl md:text-4xl font-black tracking-tight text-[#333] leading-none mb-1">{activeSDGs}</span>
                <span className="text-sm text-muted-foreground font-medium">Active Goals</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl md:text-4xl font-black tracking-tight text-[#333] leading-none mb-1">169</span>
                <span className="text-sm text-muted-foreground font-medium">Targets</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-3xl md:text-4xl font-black tracking-tight text-[#333] leading-none mb-1">{totalPubs.toLocaleString()}</span>
                <span className="text-sm text-muted-foreground font-medium">Publications</span>
              </div>
            </div>
          </div>
        </div>

        {/* Crypto Heatmap (Treemap) */}
        <div className="w-full flex justify-center bg-background pb-10 pt-2 px-2 md:px-6">
          <div className="w-full max-w-[1200px] aspect-[1/1] sm:aspect-[4/3] md:aspect-[2/1] relative bg-background border border-border/30 overflow-hidden shadow-2xl rounded-xl">
            {treemapLeaves.map((leaf) => {
              const dataItem = leaf.data;
              const num = dataItem.sdg_num;
              const info = SDG_INFO[num];
              const imageUrl = getSdgIconUrl(num) || `https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${String(num).padStart(2, '0')}.jpg`;

              const width = leaf.x1 - leaf.x0;
              const height = leaf.y1 - leaf.y0;
              const area = width * height;
              const isSmall = area < 8;
              const isVerySmall = area < 3;

              return (
                <div
                  key={num}
                  className="absolute group cursor-pointer transition-all duration-300 hover:z-50 hover:shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:scale-[1.03]"
                  style={{
                    left: `${leaf.x0}%`,
                    top: `${leaf.y0}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                    border: '1px solid rgba(255,255,255,0.15)'
                  }}
                  onClick={() => onSdgClick?.(num)}
                  title={`Goal ${num}: ${info?.name}\n${dataItem.papers?.toLocaleString()} Publications\n${dataItem.share_pct != null ? `${Number(dataItem.share_pct).toFixed(1)}% Share` : ''}`}
                >
                  {/* Background Layer (0.8 opacity by default, 1.0 on hover) */}
                  <div
                    className="absolute inset-0 transition-opacity duration-300 opacity-80 group-hover:opacity-100"
                    style={{ backgroundColor: info?.color || '#333' }}
                  />

                  {/* Base Content */}
                  <div className="w-full h-full p-1 sm:p-2 md:p-3 flex flex-row justify-center items-center text-white relative overflow-hidden transition-opacity duration-300 gap-1 sm:gap-2 md:gap-3">
                    
                    {/* Square Image on the Left, Vertically Centered */}
                    {!isVerySmall && (
                      <img
                        src={imageUrl}
                        alt={info?.name || `SDG ${num}`}
                        className={`shrink-0 object-contain rounded-sm z-10 shadow-sm ${isSmall ? 'w-5 h-5 sm:w-6 sm:h-6' : 'w-8 h-8 sm:w-10 sm:h-10 lg:w-14 lg:h-14'}`}
                        onError={(e) => {
                          const unFallback = `https://sdgs.un.org/sites/default/files/goals/E_SDG_Icons-${String(num).padStart(2, '0')}.jpg`;
                          if (e.target.src !== unFallback) {
                            e.target.src = unFallback;
                          }
                        }}
                      />
                    )}

                    {/* Text Container */}
                    <div className="flex flex-col items-center justify-center min-w-0 z-10 flex-1">
                      {!isVerySmall && (
                        <div className="flex flex-col items-center opacity-100">
                          <span className="font-bold text-xs sm:text-sm md:text-base lg:text-xl leading-none">
                            {dataItem.papers?.toLocaleString()}
                          </span>
                          {!isSmall && dataItem.share_pct != null && (
                            <span className="text-[9px] sm:text-[10px] lg:text-[12px] font-bold mt-0.5 opacity-90">
                              {Number(dataItem.share_pct).toFixed(1)}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default SocialImpactCard;
